import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../components/Layout/Header';
import { leaveAPI, employeeAPI, leaveEnhancementAPI } from '../lib/api';
import api from '../lib/api';
import { formatDate, formatCurrency, getStatusBadgeClass, capitalizeFirst } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, Clock, Calendar as CalendarIcon, Upload, Download, FileSpreadsheet, AlertCircle, ChevronLeft, ChevronRight, Info, Plus, Wallet, DollarSign, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { HelpTooltip } from '../components/HelpTooltip';

// Import extracted components
import { 
  LEAVE_TYPE_COLORS, 
  STATUS_COLORS,
  BulkImportModal,
  CancelLeaveModal,
  CompOffModal,
  EncashmentModal,
  EnhancementActionModal
} from '../components/leave';

// Import new modular components
import { LeaveCalendarView } from '../components/leave-management/LeaveCalendarView';
import { LeaveApplicationsTable, LeaveFilters } from '../components/leave-management/LeaveApplicationsTable';

const LeaveManagement = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr', 'verifier'].includes(user?.role);
  
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('applications');
  
  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarEmployee, setCalendarEmployee] = useState('all');
  
  // Bulk Import state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('approved'); // HR chooses status

  // Cancel Leave Modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLeaveId, setCancelLeaveId] = useState(null);
  const [cancelLeaveInfo, setCancelLeaveInfo] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Comp-Off & Encashment state
  const [compOffBalance, setCompOffBalance] = useState(null);
  const [myCompOffRequests, setMyCompOffRequests] = useState([]);
  const [allCompOffRequests, setAllCompOffRequests] = useState([]);
  const [myEncashmentRequests, setMyEncashmentRequests] = useState([]);
  const [allEncashmentRequests, setAllEncashmentRequests] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  
  // Comp-Off & Encashment Modals
  const [showCompOffModal, setShowCompOffModal] = useState(false);
  const [showEncashmentModal, setShowEncashmentModal] = useState(false);
  const [showEnhancementActionModal, setShowEnhancementActionModal] = useState(false);
  const [selectedEnhancementItem, setSelectedEnhancementItem] = useState(null);
  const [enhancementActionType, setEnhancementActionType] = useState('');
  const [enhancementActionReason, setEnhancementActionReason] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  
  // Forms
  const [compOffForm, setCompOffForm] = useState({
    work_date: '',
    hours_worked: 8,
    reason: '',
    project_or_task: ''
  });
  
  const [encashmentForm, setEncashmentForm] = useState({
    leave_type: 'earned',
    days_to_encash: 1,
    remarks: ''
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // Basic data for all users
      const [leaveRes, empRes] = await Promise.all([
        leaveAPI.getAll(),
        employeeAPI.getAll()
      ]);
      
      setLeaveApplications(leaveRes.data);
      setEmployees(empRes.data);
      
      // Try to fetch comp-off and encashment data (may fail for admins without employee profile)
      try {
        const [compOffReqs, compOffBal, encashReqs] = await Promise.all([
          leaveEnhancementAPI.getMyCompOffRequests(),
          leaveEnhancementAPI.getMyCompOffBalance(),
          leaveEnhancementAPI.getMyEncashmentRequests()
        ]);
        setMyCompOffRequests(compOffReqs.data || []);
        setCompOffBalance(compOffBal.data);
        setMyEncashmentRequests(encashReqs.data || []);
      } catch (e) {
        // Admin users may not have employee profiles
        setMyCompOffRequests([]);
        setCompOffBalance({ available: 0, used: 0, pending: 0 });
        setMyEncashmentRequests([]);
      }
      
      // Try to fetch leave balances
      try {
        const balRes = await leaveAPI.getLeaveBalances();
        setLeaveBalances(balRes.data || []);
      } catch (e) {
        setLeaveBalances([]);
      }
      
      // Admin/Verifier-only data
      if (isAdmin) {
        try {
          const [allCompOff, allEncash] = await Promise.all([
            leaveEnhancementAPI.getAllCompOffRequests(),
            leaveEnhancementAPI.getAllEncashmentRequests()
          ]);
          setAllCompOffRequests(allCompOff.data || []);
          setAllEncashmentRequests(allEncash.data || []);
        } catch (e) {
          setAllCompOffRequests([]);
          setAllEncashmentRequests([]);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch leave data');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (leaveId, action, rejectionReason = null) => {
    try {
      await leaveAPI.processAction(leaveId, { 
        action,
        rejection_reason: rejectionReason
      });
      toast.success(`Leave ${action === 'verify' ? 'verified' : action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process leave request');
    }
  };

  const openCancelModal = (leave) => {
    setCancelLeaveId(leave.id);
    setCancelLeaveInfo(leave);
    setCancellationReason('');
    setCancelModalOpen(true);
  };

  const handleCancelLeave = async () => {
    if (!cancelLeaveId) return;
    
    // Make reason mandatory
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    setCancelling(true);
    try {
      await api.put(`/leave/${cancelLeaveId}/cancel`, {
        cancellation_reason: cancellationReason
      });
      toast.success('Leave cancelled successfully');
      setCancelModalOpen(false);
      setCancelLeaveId(null);
      setCancelLeaveInfo(null);
      setCancellationReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel leave');
    } finally {
      setCancelling(false);
    }
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find((e) => e.id === employeeId);
    return emp?.name || emp?.first_name ? `${emp.first_name} ${emp.last_name || ''}`.trim() : 'Unknown';
  };

  const getEmployeeCode = (employeeId) => {
    const emp = employees.find((e) => e.id === employeeId);
    return emp?.employee_code || 'N/A';
  };

  const filteredApplications = leaveApplications.filter((app) => {
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchesEmployee = filterEmployee === 'all' || app.employee_id === filterEmployee;
    const empName = app.employee_name || getEmployeeName(app.employee_id);
    const empCode = getEmployeeCode(app.employee_id);
    const matchesSearch = !searchTerm || 
      empName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      empCode.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesEmployee && matchesSearch;
  });

  // Calendar data processing
  const calendarLeaves = useMemo(() => {
    let filtered = leaveApplications;
    if (calendarEmployee !== 'all') {
      filtered = filtered.filter(app => app.employee_id === calendarEmployee);
    }
    return filtered.filter(app => ['pending', 'verified', 'approved'].includes(app.status));
  }, [leaveApplications, calendarEmployee]);

  // Get leaves for a specific date
  const getLeavesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return calendarLeaves.filter(leave => {
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      const checkDate = new Date(dateStr);
      return checkDate >= start && checkDate <= end;
    });
  };

  // Get all dates that have leaves
  const datesWithLeaves = useMemo(() => {
    const dates = new Set();
    calendarLeaves.forEach(leave => {
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.add(d.toISOString().split('T')[0]);
      }
    });
    return dates;
  }, [calendarLeaves]);

  // Selected date leaves
  const selectedDateLeaves = selectedDate ? getLeavesForDate(selectedDate) : [];

  const pendingCount = leaveApplications.filter((a) => a.status === 'pending').length;
  const verifiedCount = leaveApplications.filter((a) => a.status === 'verified').length;
  const approvedCount = leaveApplications.filter((a) => a.status === 'approved').length;
  const rejectedCount = leaveApplications.filter((a) => a.status === 'rejected').length;
  const cancelledCount = leaveApplications.filter((a) => a.status === 'cancelled').length;

  // Bulk Import Functions
  const downloadTemplate = () => {
    const headers = ['employee_code', 'leave_type', 'start_date', 'end_date', 'reason'];
    const sampleData = [
      ['EMP001', 'casual', '2025-12-25', '2025-12-26', 'Holiday travel'],
      ['EMP002', 'sick', '2025-12-20', '2025-12-20', 'Medical appointment'],
    ];
    
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leave_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setImportErrors([]);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const data = lines.slice(1).map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          const row = {};
          headers.forEach((header, i) => {
            row[header] = values[i] || '';
          });
          row._rowIndex = index + 2; // For error reporting
          return row;
        });
        
        // Validate data
        const errors = [];
        const validData = data.map(row => {
          const rowErrors = [];
          
          // Check employee code exists
          const employee = employees.find(e => 
            e.employee_code === row.employee_code || 
            e.emp_code === row.employee_code
          );
          if (!employee) {
            rowErrors.push(`Employee code "${row.employee_code}" not found`);
          } else {
            row._employee_id = employee.id;
            row._employee_name = employee.name || `${employee.first_name} ${employee.last_name}`.trim();
          }
          
          // Validate leave type
          const validLeaveTypes = ['casual', 'sick', 'earned', 'compensatory', 'unpaid'];
          if (!validLeaveTypes.includes(row.leave_type?.toLowerCase())) {
            rowErrors.push(`Invalid leave type "${row.leave_type}"`);
          }
          
          // Validate dates
          const startDate = new Date(row.start_date);
          const endDate = new Date(row.end_date);
          if (isNaN(startDate.getTime())) {
            rowErrors.push(`Invalid start date "${row.start_date}"`);
          }
          if (isNaN(endDate.getTime())) {
            rowErrors.push(`Invalid end date "${row.end_date}"`);
          }
          if (startDate > endDate) {
            rowErrors.push('Start date cannot be after end date');
          }
          
          row._errors = rowErrors;
          row._valid = rowErrors.length === 0;
          
          if (rowErrors.length > 0) {
            errors.push({ row: row._rowIndex, errors: rowErrors });
          }
          
          return row;
        });
        
        setImportPreview(validData);
        setImportErrors(errors);
        
      } catch (err) {
        toast.error('Failed to parse file');
        setImportPreview([]);
      }
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    const validRows = importPreview.filter(row => row._valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }
    
    setImporting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const row of validRows) {
      try {
        await api.post('/leave/bulk-import', {
          employee_id: row._employee_id,
          leave_type: row.leave_type.toLowerCase(),
          start_date: row.start_date,
          end_date: row.end_date,
          reason: row.reason || 'Imported leave',
          status: importStatus
        });
        successCount++;
      } catch (error) {
        failCount++;
        console.error('Import failed for row:', row, error);
      }
    }
    
    setImporting(false);
    setImportModalOpen(false);
    setImportFile(null);
    setImportPreview([]);
    
    if (successCount > 0) {
      toast.success(`Imported ${successCount} leave record(s)`);
      fetchData();
    }
    if (failCount > 0) {
      toast.error(`Failed to import ${failCount} record(s)`);
    }
  };

  // Comp-Off & Encashment Functions
  const handleSubmitCompOff = async () => {
    if (!compOffForm.work_date || !compOffForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await leaveEnhancementAPI.createCompOffRequest(compOffForm);
      toast.success('Comp-off request submitted');
      setShowCompOffModal(false);
      setCompOffForm({ work_date: '', hours_worked: 8, reason: '', project_or_task: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleSubmitEncashment = async () => {
    if (!encashmentForm.leave_type || encashmentForm.days_to_encash < 1) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await leaveEnhancementAPI.createEncashmentRequest(encashmentForm);
      toast.success('Encashment request submitted');
      setShowEncashmentModal(false);
      setEncashmentForm({ leave_type: 'earned', days_to_encash: 1, remarks: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleEnhancementAction = async () => {
    if (!selectedEnhancementItem) return;
    
    try {
      const isCompOff = selectedEnhancementItem.type === 'comp_off';
      const payload = {
        action: enhancementActionType,
        remarks: enhancementActionReason
      };
      
      if (enhancementActionType === 'paid' && paymentRef) {
        payload.payment_reference = paymentRef;
      }
      
      if (isCompOff) {
        await leaveEnhancementAPI.processCompOff(selectedEnhancementItem.id, payload);
      } else {
        await leaveEnhancementAPI.processEncashment(selectedEnhancementItem.id, payload);
      }
      
      toast.success(`Request ${enhancementActionType}`);
      setShowEnhancementActionModal(false);
      setSelectedEnhancementItem(null);
      setEnhancementActionReason('');
      setPaymentRef('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const openEnhancementAction = (item, type, action) => {
    setSelectedEnhancementItem({ ...item, type });
    setEnhancementActionType(action);
    setEnhancementActionReason('');
    setPaymentRef('');
    setShowEnhancementActionModal(true);
  };

  const getEnhancementStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      paid: 'bg-blue-100 text-blue-700',
      credited: 'bg-green-100 text-green-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  // Stats for Comp-Off & Encashment
  const pendingCompOff = isAdmin 
    ? allCompOffRequests.filter(r => r.status === 'pending').length
    : myCompOffRequests.filter(r => r.status === 'pending').length;
  
  const pendingEncashment = isAdmin
    ? allEncashmentRequests.filter(r => r.status === 'pending' || r.status === 'approved').length
    : myEncashmentRequests.filter(r => r.status === 'pending' || r.status === 'approved').length;

  // Custom day renderer for calendar
  const renderDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const hasLeaves = datesWithLeaves.has(dateStr);
    const dayLeaves = hasLeaves ? getLeavesForDate(date) : [];
    
    if (!hasLeaves) {
      return <span>{date.getDate()}</span>;
    }
    
    // Get unique leave types for this date
    const leaveTypes = [...new Set(dayLeaves.map(l => l.leave_type))];
    const mainType = leaveTypes[0];
    const colors = LEAVE_TYPE_COLORS[mainType] || LEAVE_TYPE_COLORS.casual;
    
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <span className="relative z-10">{date.getDate()}</span>
        <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-0.5`}>
          {leaveTypes.slice(0, 3).map((type) => (
            <div 
              key={type} 
              className={`w-1.5 h-1.5 rounded-full ${LEAVE_TYPE_COLORS[type]?.bg || 'bg-slate-400'}`}
            />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="leave-management-page">
      <Header 
        title="Leave Management" 
        subtitle="Review and process leave requests"
        action={
          <div className="flex items-center gap-2">
            <HelpTooltip moduleId="leave-management" />
            <Button onClick={() => setImportModalOpen(true)} variant="outline" className="gap-2">
              <Upload className="w-4 h-4" />
              Bulk Import
            </Button>
          </div>
        }
      />

      <div className="p-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="dashboard-card stat-accent-warning">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Pending</p>
                <p className="text-2xl font-heading font-semibold text-amber-600">{pendingCount}</p>
              </div>
            </div>
          </div>
          <div className="dashboard-card stat-accent-info">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Verified</p>
                <p className="text-2xl font-heading font-semibold text-blue-600">{verifiedCount}</p>
              </div>
            </div>
          </div>
          <div className="dashboard-card stat-accent-success">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Approved</p>
                <p className="text-2xl font-heading font-semibold text-emerald-600">{approvedCount}</p>
              </div>
            </div>
          </div>
          <div className="dashboard-card stat-accent-danger">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-100">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Rejected</p>
                <p className="text-2xl font-heading font-semibold text-red-600">{rejectedCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="applications" className="gap-2">
              <Clock className="w-4 h-4" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2">
              <CalendarIcon className="w-4 h-4" />
              Leave Calendar
            </TabsTrigger>
            <TabsTrigger value="encashment" className="gap-2" data-testid="encashment-tab">
              <Wallet className="w-4 h-4" />
              Encashment
              {pendingEncashment > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">
                  {pendingEncashment}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Search input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search employee..."
                  className="pl-9 w-48"
                  data-testid="search-employee"
                />
              </div>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="w-48" data-testid="filter-employee">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name || `${emp.first_name} ${emp.last_name}`.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Leave Applications Table */}
            <div className="dashboard-card overflow-hidden">
              <table className="data-table" data-testid="leave-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Leave Type</th>
                    <th>Duration</th>
                    <th>Dates</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((app) => (
                    <tr key={app.id} data-testid={`leave-row-${app.id}`}>
                      <td>
                        <div>
                          <p className="font-medium text-slate-900">{app.employee_name || getEmployeeName(app.employee_id)}</p>
                          <p className="text-xs text-slate-500">{getEmployeeCode(app.employee_id)}</p>
                        </div>
                      </td>
                      <td>
                        <Badge variant="outline" className={LEAVE_TYPE_COLORS[app.leave_type]?.text}>
                          {capitalizeFirst(app.leave_type)}
                        </Badge>
                      </td>
                      <td>{app.days} day(s)</td>
                      <td>
                        <div className="text-sm">
                          <p>{formatDate(app.start_date)}</p>
                          {app.start_date !== app.end_date && (
                            <p className="text-slate-500">to {formatDate(app.end_date)}</p>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="text-sm text-slate-600 max-w-xs truncate">{app.reason}</p>
                          {app.cancellation_reason && (
                            <p className="text-xs text-orange-600 mt-1">
                              Cancelled: {app.cancellation_reason}
                            </p>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={getStatusBadgeClass(app.status)}>
                          {capitalizeFirst(app.status)}
                        </span>
                      </td>
                      <td>
                        {['pending', 'verified'].includes(app.status) && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-600 hover:bg-emerald-50"
                              onClick={() => handleAction(app.id, 'approve')}
                              data-testid={`approve-${app.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleAction(app.id, 'reject')}
                              data-testid={`reject-${app.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                        {app.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-orange-600 hover:bg-orange-50"
                            onClick={() => openCancelModal(app)}
                            data-testid={`cancel-${app.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel Leave
                          </Button>
                        )}
                        {app.status === 'cancelled' && (
                          <span className="text-sm text-slate-400">Cancelled</span>
                        )}
                        {app.status === 'rejected' && (
                          <span className="text-sm text-slate-400">Rejected</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredApplications.length === 0 && (
                <div className="empty-state py-12">
                  <CalendarIcon className="w-12 h-12 text-slate-300" />
                  <h3>No leave applications found</h3>
                  <p>No applications match your filters</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-6">
            <LeaveCalendarView
              leaveApplications={leaveApplications}
              employees={employees}
              calendarMonth={calendarMonth}
              setCalendarMonth={setCalendarMonth}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              calendarEmployee={calendarEmployee}
              setCalendarEmployee={setCalendarEmployee}
            />
          </TabsContent>

          {/* Comp-Off Tab */}
          <TabsContent value="compoff" className="space-y-6">
            {/* Balance Card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Available Balance</p>
                    <p className="text-2xl font-bold text-emerald-600">{compOffBalance?.balance || 0} days</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-100">
                    <Plus className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </div>
              <div className="dashboard-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase">Pending Requests</p>
                    <p className="text-2xl font-bold text-amber-600">{pendingCompOff}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-100">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end">
                <Button onClick={() => setShowCompOffModal(true)} data-testid="request-compoff-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Request Comp-Off
                </Button>
              </div>
            </div>

            {/* Comp-Off Requests Table */}
            <div className="dashboard-card">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                {isAdmin ? 'All Comp-Off Requests' : 'My Comp-Off Requests'}
              </h3>
              
              {(isAdmin ? allCompOffRequests : myCompOffRequests).length === 0 ? (
                <div className="empty-state py-8">
                  <Plus className="w-12 h-12 text-slate-300" />
                  <h3>No Comp-Off Requests</h3>
                  <p>No comp-off requests found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        {isAdmin && <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>}
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Work Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Hours</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Reason</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isAdmin ? allCompOffRequests : myCompOffRequests).map((req) => (
                        <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                          {isAdmin && (
                            <td className="py-3 px-4">
                              <p className="font-medium">{req.employee_name}</p>
                            </td>
                          )}
                          <td className="py-3 px-4">{formatDate(req.work_date)}</td>
                          <td className="py-3 px-4">{req.hours_worked}h</td>
                          <td className="py-3 px-4 max-w-xs truncate">{req.reason}</td>
                          <td className="py-3 px-4">{getEnhancementStatusBadge(req.status)}</td>
                          <td className="py-3 px-4">
                            {isAdmin && req.status === 'pending' && (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => openEnhancementAction(req, 'comp_off', 'approve')}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => openEnhancementAction(req, 'comp_off', 'reject')}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                            {req.status === 'approved' && (
                              <span className="text-xs text-emerald-600">+{req.days_credited || 1} day credited</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Encashment Tab */}
          <TabsContent value="encashment" className="space-y-6">
            {/* Balance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {leaveBalances.map((lb) => (
                <div key={lb.id || lb.leave_type} className="dashboard-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase">{capitalizeFirst(lb.leave_type)}</p>
                      <p className="text-2xl font-bold text-blue-600">{lb.balance || 0} days</p>
                      <p className="text-xs text-slate-400">Encashable: {lb.encashable || 0}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-blue-100">
                      <Wallet className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-end">
                <Button onClick={() => setShowEncashmentModal(true)} data-testid="request-encashment-btn">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Request Encashment
                </Button>
              </div>
            </div>

            {/* Encashment Requests Table */}
            <div className="dashboard-card">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">
                {isAdmin ? 'All Encashment Requests' : 'My Encashment Requests'}
              </h3>
              
              {(isAdmin ? allEncashmentRequests : myEncashmentRequests).length === 0 ? (
                <div className="empty-state py-8">
                  <Wallet className="w-12 h-12 text-slate-300" />
                  <h3>No Encashment Requests</h3>
                  <p>No encashment requests found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        {isAdmin && <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Employee</th>}
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Leave Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Days</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isAdmin ? allEncashmentRequests : myEncashmentRequests).map((req) => (
                        <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50">
                          {isAdmin && (
                            <td className="py-3 px-4">
                              <p className="font-medium">{req.employee_name}</p>
                            </td>
                          )}
                          <td className="py-3 px-4 capitalize">{req.leave_type}</td>
                          <td className="py-3 px-4">{req.days_to_encash}</td>
                          <td className="py-3 px-4 font-medium text-emerald-600">{formatCurrency(req.amount || 0)}</td>
                          <td className="py-3 px-4">{getEnhancementStatusBadge(req.status)}</td>
                          <td className="py-3 px-4">
                            {isAdmin && req.status === 'pending' && (
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => openEnhancementAction(req, 'encashment', 'approve')}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => openEnhancementAction(req, 'encashment', 'reject')}
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            )}
                            {isAdmin && req.status === 'approved' && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => openEnhancementAction(req, 'encashment', 'paid')}
                              >
                                <DollarSign className="w-3 h-3 mr-1" />
                                Mark Paid
                              </Button>
                            )}
                            {req.status === 'paid' && req.payment_reference && (
                              <span className="text-xs text-blue-600">Ref: {req.payment_reference}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals - Using extracted components */}
      <BulkImportModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        importStatus={importStatus}
        setImportStatus={setImportStatus}
        importFile={importFile}
        importPreview={importPreview}
        importErrors={importErrors}
        importing={importing}
        onFileUpload={handleFileUpload}
        onDownloadTemplate={downloadTemplate}
        onBulkImport={handleBulkImport}
      />

      <CancelLeaveModal
        open={cancelModalOpen}
        onOpenChange={setCancelModalOpen}
        leaveInfo={cancelLeaveInfo}
        cancellationReason={cancellationReason}
        setCancellationReason={setCancellationReason}
        cancelling={cancelling}
        onCancel={handleCancelLeave}
        getEmployeeName={getEmployeeName}
      />

      <CompOffModal
        open={showCompOffModal}
        onOpenChange={setShowCompOffModal}
        form={compOffForm}
        setForm={setCompOffForm}
        onSubmit={handleSubmitCompOff}
      />

      <EncashmentModal
        open={showEncashmentModal}
        onOpenChange={setShowEncashmentModal}
        form={encashmentForm}
        setForm={setEncashmentForm}
        onSubmit={handleSubmitEncashment}
      />

      <EnhancementActionModal
        open={showEnhancementActionModal}
        onOpenChange={setShowEnhancementActionModal}
        selectedItem={selectedEnhancementItem}
        actionType={enhancementActionType}
        reason={enhancementActionReason}
        setReason={setEnhancementActionReason}
        paymentRef={paymentRef}
        setPaymentRef={setPaymentRef}
        onConfirm={handleEnhancementAction}
        formatDate={formatDate}
      />
    </div>
  );
};

export default LeaveManagement;
